<template>
  <div class="min-h-screen bg-black text-white font-sans flex items-center justify-center">
    <div class="w-full max-w-lg px-4">
      <!-- Logo -->
      <div class="flex justify-center mb-4">
        <img src="/icon.png" alt="Ploppin Logo" class="w-12 h-12 rounded-2xl" />
      </div>

      <div class="bg-[#1A2035]/50 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
        <!-- Success Message After Registration -->
        <div v-if="registrationComplete" class="text-center">
          <div class="text-gray-400 text-4xl mb-3">📧</div>
          <h1 class="text-white text-xl font-bold mb-2 lowercase">verify your email</h1>
          <p class="text-gray-400 mb-3 lowercase">we've sent a verification link to:</p>
          <p class="text-gray-400 font-medium mb-4">{{ email }}</p>
          <p class="text-gray-400 text-sm mb-4 lowercase">please check your inbox and click the link to verify your account.</p>
          
          <!-- Resend Verification Email -->
          <div class="mt-3">
            <button
              @click="handleResendVerification"
              :disabled="resendLoading"
              class="text-gray-400 hover:text-white text-sm flex items-center justify-center gap-1 mx-auto disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg v-if="resendLoading" class="animate-spin h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ resendLoading ? 'Sending...' : 'Didn\'t receive the email? Resend' }}
            </button>
          </div>
          
          <div class="mt-4">
            <NuxtLink 
              to="/login" 
              class="bg-gray-700 hover:bg-gray-600 border border-gray-600 px-6 py-2 rounded-lg text-white font-medium transition-colors"
            >
              Go to Login
            </NuxtLink>
          </div>
        </div>

        <!-- Already Verified Account Message -->
        <div v-else-if="alreadyVerified" class="text-center">
          <div class="text-green-400 text-4xl mb-3">✓</div>
          <h1 class="text-white text-xl font-bold mb-2 lowercase">account already exists</h1>
          <p class="text-gray-400 mb-4 lowercase">the email <span class="text-gray-400 font-medium">{{ email }}</span> is already registered and verified.</p>
          <div class="mt-4">
            <NuxtLink 
              to="/login" 
              class="bg-gray-700 hover:bg-gray-600 border border-gray-600 px-6 py-2 rounded-lg text-white font-medium transition-colors"
            >
              Sign In
            </NuxtLink>
          </div>
        </div>

        <!-- Registration Form -->
        <div v-else>
          <!-- Title -->
          <h1 class="text-white text-xl font-bold text-center mb-2 lowercase">create account</h1>
          <p class="text-gray-400 text-center mb-6 lowercase">get started with ploppin'</p>

          <!-- Error Alert -->
          <div v-if="error" class="mb-4 p-3 bg-red-500/20 border border-red-500 rounded-lg text-red-200 text-sm">
            {{ error }}
          </div>

          <!-- Registration Form -->
          <form @submit.prevent="handleSubmit" class="space-y-4">
            <!-- Google Sign Up -->
            <button 
              type="button"
              :disabled="isLoading"
              class="w-full flex items-center justify-center gap-3 bg-gray-700 hover:bg-gray-500 text-white rounded-lg p-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              @click="handleGoogleSignup"
            >
              <img src="https://www.google.com/favicon.ico" alt="Google" class="w-4 h-4" />
              Sign up with Google
            </button>

            <div class="relative flex items-center justify-center">
              <div class="border-t border-gray-600 w-full"></div>
              <span class="bg-[#1A2035] text-gray-400 px-4 text-sm">or</span>
              <div class="border-t border-gray-600 w-full"></div>
            </div>

            <!-- Email Input -->
            <div>
              <label class="block text-gray-300 text-sm font-medium mb-1">Email</label>
              <input 
                type="email"
                v-model="email"
                :disabled="isLoading"
                class="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white placeholder-gray-400 focus:outline-none focus:border-gray-400 disabled:opacity-50"
                placeholder="Enter your email"
                :class="{'border-red-500': validationErrors.email}"
              />
              <p v-if="validationErrors.email" class="mt-1 text-sm text-red-400">{{ validationErrors.email }}</p>
            </div>

            <!-- Password Input -->
            <div>
              <label class="block text-gray-300 text-sm font-medium mb-1">Password</label>
              <div class="relative">
                <input 
                  :type="showPassword ? 'text' : 'password'"
                  v-model="password"
                  :disabled="isLoading"
                  class="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white placeholder-gray-400 focus:outline-none focus:border-gray-400 disabled:opacity-50"
                  placeholder="Create a password"
                  :class="{'border-red-500': validationErrors.password}"
                />
                <button 
                  type="button"
                  @click="showPassword = !showPassword"
                  class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300"
                >
                  <span v-if="showPassword">👁️</span>
                  <span v-else>👁️‍🗨️</span>
                </button>
              </div>
              <p class="mt-1 text-sm" :class="validationErrors.password ? 'text-red-400' : 'text-gray-400'">
                Must be at least 8 characters long
              </p>
            </div>

            <!-- Submit Button -->
            <button 
              type="submit"
              :disabled="isLoading"
              class="w-full bg-gray-700 hover:bg-gray-600 border border-gray-600 py-2.5 rounded-lg flex items-center justify-center gap-2 transition-colors backdrop-blur-sm text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg v-if="isLoading" class="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span v-if="isLoading">Creating account...</span>
              <span v-else>Create Account</span>
            </button>
          </form>

          <!-- Links -->
          <div class="mt-4 text-center">
            <p class="text-gray-400">
              Already have an account? 
              <NuxtLink to="/login" class="text-gray-400 hover:text-white">Sign In</NuxtLink>
            </p>
          </div>
        </div>
      </div>

      <!-- Back to Home -->
      <div class="mt-4 text-center">
        <NuxtLink to="/" class="text-gray-400 hover:text-white transition-colors lowercase">
          ← back to home
        </NuxtLink>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { useAuth } from '~/composables/useAuth'

const { $pb } = useNuxtApp()

const email = ref('')
const password = ref('')
const showPassword = ref(false)
const isLoading = ref(false)
const error = ref('')
const registrationComplete = ref(false)
const alreadyVerified = ref(false)
const resendLoading = ref(false)
const validationErrors = reactive({
  email: '',
  password: ''
})

// Use composables instead of store
const { loginWithGoogle, registerWithEmail, resendVerificationEmail } = useAuth()

const handleGoogleSignup = async () => {
  try {
    clearErrors()
    await loginWithGoogle()
    await navigateTo('/app')
  } catch (err) {
    error.value = err.message || 'Failed to sign up with Google'
  }
}

const clearErrors = () => {
  error.value = ''
  validationErrors.email = ''
  validationErrors.password = ''
}

const validateForm = () => {
  let isValid = true
  clearErrors()

  if (!email.value.trim()) {
    validationErrors.email = 'Email is required'
    isValid = false
  }

  if (!password.value) {
    validationErrors.password = 'Password is required'
    isValid = false
  } else if (password.value.length < 8) {
    validationErrors.password = 'Password must be at least 8 characters long'
    isValid = false
  }

  if (!isValid) {
    error.value = 'Please fix the errors below'
  }

  return isValid
}


const handleSubmit = async () => {
  if (!validateForm()) {
    return
  }

  try {
    isLoading.value = true
    
    // Always try to register - let PocketBase handle duplicates
    await registerWithEmail(email.value, password.value)
    registrationComplete.value = true
    
  } catch (err) {
    // Handle the specific error for duplicate emails
    if (err.data?.data?.email?.code === 'validation_invalid_email' || 
        err.message?.includes('email already exists') ||
        err.status === 400) {
      
      // For duplicate emails, we can't know if they're verified or not
      // So we show a generic message and offer to resend verification
      error.value = 'An account with this email already exists. Try signing in instead.'
      
    } else {
      // Handle other validation errors
      error.value = err.message || 'Failed to create account'
      
      // Map backend validation errors to form fields
      if (err.data?.data) {
        const fieldErrors = err.data.data
        
        if (fieldErrors.email) {
          validationErrors.email = fieldErrors.email.message || 'Invalid email'
        }
        
        if (fieldErrors.password) {
          validationErrors.password = fieldErrors.password.message || 'Invalid password'
        }
      }
    }
  } finally {
    isLoading.value = false
  }
}

const handleResendVerification = async () => {
  if (!email.value) return

  try {
    resendLoading.value = true
    await resendVerificationEmail(email.value)
    // Show success toast or message
    alert('Verification email sent! Please check your inbox.')
  } catch (err) {
    error.value = err.message || 'Failed to resend verification email'
  } finally {
    resendLoading.value = false
  }
}
</script>